import { Component, EventEmitter, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { AuthService } from '@app/services/auth.service';

type CheckState = 'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved';

const RESERVED_USERNAMES = [
  'pxlvd', 'admin', 'administrator', 'root', 'system', 'support', 'help',
  'staff', 'moderator', 'mod', 'superuser', 'owner', 'official', 'security',
  'bot', 'api', 'null', 'undefined', 'anonymous', 'guest', 'test', 'demo',
  'info', 'contact', 'team', 'dev', 'developer', 'me', 'you', 'user',
];

@Component({
  selector: 'app-username-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './username-picker.html',
  styleUrls: ['./username-picker.css'],
})
export class UsernamePicker implements OnDestroy {
  @Output() usernameSet = new EventEmitter<string>();

  username = '';
  checkState: CheckState = 'idle';
  saving = false;
  errorMsg = '';

  private input$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(private auth: AuthService) {
    this.input$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(val => this.checkAvailability(val));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onInput(): void {
    const val = this.username.toLowerCase().trim();
    this.username = val;
    this.errorMsg = '';

    if (!val) { this.checkState = 'idle'; return; }
    if (!this.isValidFormat(val)) { this.checkState = 'invalid'; return; }
    if (RESERVED_USERNAMES.includes(val)) { this.checkState = 'reserved'; return; }
    this.checkState = 'checking';
    this.input$.next(val);
  }

  private isValidFormat(val: string): boolean {
    return /^[a-z0-9_-]{3,30}$/.test(val);
  }

  private async checkAvailability(val: string): Promise<void> {
    if (!this.isValidFormat(val)) { this.checkState = 'invalid'; return; }
    if (RESERVED_USERNAMES.includes(val)) { this.checkState = 'reserved'; return; }
    const available = await this.auth.checkUsername(val);
    this.checkState = available ? 'available' : 'taken';
  }

  async onConfirm(): Promise<void> {
    const val = this.username.toLowerCase().trim();
    if (this.checkState !== 'available') return;
    this.saving = true;
    this.errorMsg = '';
    const { error } = await this.auth.updateUsername(val);
    this.saving = false;
    if (error) {
      this.errorMsg = error.message?.includes('unique') ? 'Username already taken.' : 'Something went wrong. Try again.';
      this.checkState = 'taken';
      return;
    }
    this.usernameSet.emit(val);
  }

  get canConfirm(): boolean {
    return this.checkState === 'available' && !this.saving;
  }

  get hintText(): string {
    switch (this.checkState) {
      case 'checking':   return 'Checking availability...';
      case 'available':  return `@${this.username} is available ✓`;
      case 'taken':      return `@${this.username} is already taken`;
      case 'reserved':   return `@${this.username} is a reserved username`;
      case 'invalid':    return 'Letters, numbers, _ and - only. Min 3 chars.';
      default:           return '';
    }
  }

  get hintClass(): string {
    switch (this.checkState) {
      case 'available': return 'hint-ok';
      case 'taken':
      case 'reserved':
      case 'invalid':   return 'hint-err';
      default:          return 'hint-dim';
    }
  }
}
